/**
 * Elasta SafeSip Theme - Main JavaScript
 * Premium dark Shopify theme with neon green accents
 */

(function() {
  'use strict';

  // Theme namespace
  window.SpiLa = window.SpiLa || {};

  /**
   * Utility Functions
   */
  SpiLa.utils = {
    // Debounce function
    debounce: function(func, wait) {
      let timeout;
      return function(...args) {
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(this, args), wait);
      };
    },

    // Format money
    formatMoney: function(cents, format) {
      if (typeof cents === 'string') {
        cents = cents.replace('.', '');
      }
      const value = cents / 100;
      return '$' + value.toFixed(2);
    },

    // Fetch wrapper
    fetchJSON: async function(url, options = {}) {
      const response = await fetch(url, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...options.headers
        }
      });
      return response.json();
    }
  };

  /**
   * Cart functionality
   */
  SpiLa.cart = {
    init: function() {
      this.bindEvents();
    },

    bindEvents: function() {
      document.addEventListener('submit', (e) => {
        if (e.target.matches('.product-form')) {
          e.preventDefault();
          this.addToCart(e.target);
        }
      });
    },

    addToCart: async function(form) {
      const formData = new FormData(form);
      const submitBtn = form.querySelector('[type="submit"]');
      const originalText = submitBtn.textContent;

      // Restore the button after feedback, unless the variant picker has
      // since marked the selected variant as sold out.
      const restoreBtn = () => {
        if (submitBtn.dataset.soldout === '1') {
          submitBtn.textContent = 'Sold Out';
          submitBtn.disabled = true;
        } else {
          submitBtn.textContent = originalText;
          submitBtn.disabled = false;
        }
      };

      submitBtn.textContent = 'Adding...';
      submitBtn.disabled = true;

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData
        });

        const data = await response.json();

        if (response.ok) {
          this.updateCartCount();
          submitBtn.textContent = 'Added!';
          setTimeout(restoreBtn, 2000);
        } else {
          throw new Error(data.message || 'Error adding to cart');
        }
      } catch (error) {
        console.error('Cart error:', error);
        submitBtn.textContent = 'Error';
        setTimeout(restoreBtn, 2000);
      }
    },

    updateCartCount: async function() {
      try {
        const cart = await SpiLa.utils.fetchJSON('/cart.js');
        const countElements = document.querySelectorAll('#cart-count, .cart-count');
        countElements.forEach(el => {
          el.textContent = cart.item_count;
        });
      } catch (error) {
        console.error('Error updating cart count:', error);
      }
    }
  };

  /**
   * Header functionality
   */
  SpiLa.header = {
    init: function() {
      this.header = document.querySelector('.header');
      if (!this.header) return;

      this.lastScrollY = window.scrollY;
      this.bindEvents();
    },

    bindEvents: function() {
      window.addEventListener('scroll', SpiLa.utils.debounce(() => {
        this.handleScroll();
      }, 10));
    },

    handleScroll: function() {
      const currentScrollY = window.scrollY;

      if (currentScrollY > 100) {
        this.header.classList.add('scrolled');
      } else {
        this.header.classList.remove('scrolled');
      }

      this.lastScrollY = currentScrollY;
    }
  };

  /**
   * Smooth scroll for anchor links
   */
  SpiLa.smoothScroll = {
    init: function() {
      document.querySelectorAll('a[href^="#"]').forEach(anchor => {
        anchor.addEventListener('click', (e) => {
          const href = anchor.getAttribute('href');
          if (href === '#') return;

          const target = document.querySelector(href);
          if (target) {
            e.preventDefault();
            const headerHeight = document.querySelector('.header')?.offsetHeight || 0;
            const targetPosition = target.getBoundingClientRect().top + window.scrollY - headerHeight - 20;

            window.scrollTo({
              top: targetPosition,
              behavior: 'smooth'
            });
          }
        });
      });
    }
  };

  /**
   * Intersection Observer for animations
   */
  SpiLa.animations = {
    init: function() {
      const animatedElements = document.querySelectorAll('.animate-on-scroll');
      if (!animatedElements.length) return;

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animated');
            observer.unobserve(entry.target);
          }
        });
      }, {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      });

      animatedElements.forEach(el => observer.observe(el));
    }
  };

  /**
   * Newsletter form
   */
  SpiLa.newsletter = {
    init: function() {
      const forms = document.querySelectorAll('.footer-newsletter-form, .newsletter-form');
      forms.forEach(form => {
        form.addEventListener('submit', (e) => this.handleSubmit(e, form));
      });
    },

    handleSubmit: async function(e, form) {
      e.preventDefault();
      const email = form.querySelector('input[type="email"]').value;
      const submitBtn = form.querySelector('button[type="submit"]');
      const originalHTML = submitBtn.innerHTML;

      submitBtn.innerHTML = '...';
      submitBtn.disabled = true;

      // Shopify's contact form handles the submission
      // This is just for UX feedback
      setTimeout(() => {
        submitBtn.innerHTML = '✓';
        setTimeout(() => {
          submitBtn.innerHTML = originalHTML;
          submitBtn.disabled = false;
          form.reset();
        }, 2000);
      }, 1000);
    }
  };

  /**
   * Lazy loading images
   */
  SpiLa.lazyLoad = {
    init: function() {
      if ('loading' in HTMLImageElement.prototype) {
        // Native lazy loading supported
        document.querySelectorAll('img[loading="lazy"]').forEach(img => {
          if (img.dataset.src) {
            img.src = img.dataset.src;
          }
        });
      } else {
        // Fallback for older browsers
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const img = entry.target;
              if (img.dataset.src) {
                img.src = img.dataset.src;
              }
              observer.unobserve(img);
            }
          });
        });

        document.querySelectorAll('img[data-src]').forEach(img => {
          observer.observe(img);
        });
      }
    }
  };

  /**
   * Initialize all modules
   */
  SpiLa.init = function() {
    this.cart.init();
    this.header.init();
    this.smoothScroll.init();
    this.animations.init();
    this.newsletter.init();
    this.lazyLoad.init();
  };

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => SpiLa.init());
  } else {
    SpiLa.init();
  }

})();
